import { useEffect, useState } from "react";
import { Search, ArrowLeft, Ban, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api, errText, formatPrice, formatDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Badge, TICKET_STATUS, EMAIL_STATUS, card, inputCls, btnGhost, fmtDateTime } from "./common";

function BanDialog({ user, onClose, onDone }) {
  const isBan = user.status !== "BANNED";
  const [reason, setReason] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/customers/${user.id}/ban`, {
        action: isBan ? "BAN" : "UNBAN", reason: reason.trim(), send_email: sendEmail,
      });
      toast.success(isBan
        ? `Konto gesperrt${data.email_sent ? " – E-Mail an Kunden versendet" : ""}`
        : `Konto entsperrt${data.email_sent ? " – E-Mail an Kunden versendet" : ""}`);
      onDone();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-md p-5 space-y-4`} onClick={(e) => e.stopPropagation()} data-testid="ban-dialog">
        <div>
          <h3 className="font-bold text-base flex items-center gap-2">
            {isBan ? <Ban className="w-4 h-4 text-red-600" /> : <Unlock className="w-4 h-4 text-green-600" />}
            {isBan ? "Konto sperren" : "Konto entsperren"}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {user.name || user.username} · {user.email}
          </p>
        </div>
        {isBan && (
          <p className="text-xs bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">
            Der Kunde kann sich danach nicht mehr anmelden. Alle aktiven Sitzungen werden sofort beendet.
          </p>
        )}
        <div>
          <label className="text-xs font-bold block mb-1.5">{isBan ? "Grund der Sperrung *" : "Hinweis an den Kunden *"}</label>
          <textarea className={`${inputCls} min-h-[90px] resize-y`} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={isBan ? "z. B. Verstoß gegen unsere AGB: Verkauf gefälschter Artikel…" : "z. B. Nach Prüfung deines Falls heben wir die Sperrung auf…"}
            data-testid="ban-reason-input" />
          <p className="text-[10px] text-muted-foreground mt-1">Wird dem Kunden per E-Mail mitgeteilt und intern dokumentiert (mind. 5 Zeichen).</p>
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-[#FF6B35]" data-testid="ban-send-email-toggle" />
          E-Mail mit Begründung an den Kunden senden
        </label>
        <div className="flex gap-2 justify-end">
          <button className={btnGhost} onClick={onClose} data-testid="ban-cancel-btn">Abbrechen</button>
          <button disabled={busy || reason.trim().length < 5} onClick={submit} data-testid="ban-confirm-btn"
            className={`text-sm font-bold rounded-full px-4 py-2 text-white disabled:opacity-50 ${isBan ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`}>
            {busy ? "Wird ausgeführt…" : isBan ? "Jetzt sperren" : "Jetzt entsperren"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, count }) {
  return (
    <div className={`${card} p-5`}>
      <p className="font-bold text-sm mb-3">{title}{count != null && <span className="text-muted-foreground font-normal"> ({count})</span>}</p>
      {children}
    </div>
  );
}

function Profile({ userId, onBack, openTicket }) {
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [banOpen, setBanOpen] = useState(false);
  const isAdmin = me?.role === "ADMIN" || me?.role === "SUPER_ADMIN";
  const load = () => api.get(`/admin/customers/${userId}`).then(({ data }) => setData(data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, [userId]); // eslint-disable-line
  if (!data) return <div className="h-60 bg-muted rounded-2xl animate-pulse" />;
  const u = data.user;
  const canBan = isAdmin && u.role !== "SUPER_ADMIN" && u.id !== me?.id;
  return (
    <div className="space-y-4" data-testid="customer-profile">
      {banOpen && <BanDialog user={u} onClose={() => setBanOpen(false)} onDone={() => { setBanOpen(false); load(); }} />}
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Zurück zur Suche</button>
      <div className={`${card} p-5`}>
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <h2 className="font-bold text-lg">{u.name || u.username} <span className="text-sm text-muted-foreground font-normal">@{u.username}</span></h2>
            <p className="text-sm text-muted-foreground">{u.email} {u.email_verified ? "· verifiziert" : "· nicht verifiziert"}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${u.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{u.status === "BANNED" ? "GESPERRT" : u.status}</span>
            {canBan && (
              <button onClick={() => setBanOpen(true)} data-testid="customer-ban-btn"
                className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 border ${u.status === "BANNED" ? "border-green-300 text-green-700 hover:bg-green-50" : "border-red-300 text-red-600 hover:bg-red-50"}`}>
                {u.status === "BANNED" ? <><Unlock className="w-3.5 h-3.5" /> Entsperren</> : <><Ban className="w-3.5 h-3.5" /> Sperren</>}
              </button>
            )}
          </div>
        </div>
        {u.status === "BANNED" && u.ban_reason && (
          <div className="mt-3 text-xs bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2" data-testid="ban-reason-box">
            <b>Gesperrt{u.banned_by ? ` von ${u.banned_by}` : ""}{u.banned_at ? ` am ${formatDate(u.banned_at)}` : ""}:</b> {u.ban_reason}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
          <div><p className="text-xs text-muted-foreground">Registriert</p><p className="font-semibold">{formatDate(u.created_at)}</p></div>
          <div><p className="text-xs text-muted-foreground">Verkäufe</p><p className="font-semibold">{u.stats?.sales_count ?? 0}</p></div>
          <div><p className="text-xs text-muted-foreground">Käufe</p><p className="font-semibold">{u.stats?.purchases_count ?? 0}</p></div>
          <div><p className="text-xs text-muted-foreground">Inserate</p><p className="font-semibold">{data.items_total}</p></div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Supporttickets" count={data.tickets.length}>
          <div className="space-y-2">
            {data.tickets.length === 0 && <p className="text-sm text-muted-foreground">Keine Tickets.</p>}
            {data.tickets.slice(0, 8).map((t) => (
              <button key={t.id} onClick={() => openTicket(t.id)} className="w-full flex items-center gap-2 text-left text-sm hover:bg-secondary/50 rounded-lg px-2 py-1.5">
                <span className="font-mono text-xs font-bold">{t.ticket_id}</span>
                <span className="flex-1 truncate">{t.subject}</span>
                <Badge map={TICKET_STATUS} value={t.status} />
              </button>
            ))}
          </div>
        </Section>
        <Section title="E-Mail-Verkehr" count={data.emails.length}>
          <div className="space-y-1.5">
            {data.emails.length === 0 && <p className="text-sm text-muted-foreground">Keine E-Mails.</p>}
            {data.emails.slice(0, 8).map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-sm">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${e.direction === "IN" ? "bg-blue-100 text-blue-700" : "bg-secondary"}`}>{e.direction === "IN" ? "Ein" : "Aus"}</span>
                <span className="flex-1 truncate">{e.subject || "(Kein Betreff)"}</span>
                <Badge map={EMAIL_STATUS} value={e.status} />
                <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(e.date)}</span>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Letzte Inserate" count={data.items_total}>
          <div className="space-y-1.5 text-sm">
            {data.items.length === 0 && <p className="text-sm text-muted-foreground">Keine Inserate.</p>}
            {data.items.map((i) => (
              <div key={i.id} className="flex items-center gap-2"><span className="flex-1 truncate">{i.title}</span><span className="text-xs font-bold">{formatPrice(i.price)}</span><span className="text-[10px] font-bold bg-secondary px-1.5 py-0.5 rounded">{i.status}</span></div>
            ))}
          </div>
        </Section>
        <Section title="Bestellungen">
          <div className="space-y-1.5 text-sm">
            {data.orders.length === 0 && <p className="text-sm text-muted-foreground">Keine Bestellungen.</p>}
            {data.orders.map((o) => (
              <div key={o.id} className="flex items-center gap-2">
                <span className="text-[10px] font-bold bg-secondary px-1.5 py-0.5 rounded">{o.buyer_id === userId ? "Kauf" : "Verkauf"}</span>
                <span className="flex-1 truncate">{o.item_title || o.id}</span>
                <span className="text-xs">{o.status}</span>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Meldungen (von / über Nutzer)" count={data.reports_made.length + data.reports_against.length}>
          <div className="space-y-1.5 text-sm">
            {data.reports_made.length + data.reports_against.length === 0 && <p className="text-sm text-muted-foreground">Keine Meldungen.</p>}
            {data.reports_made.map((r) => <p key={r.id} className="text-xs"><b>Gemeldet:</b> {r.entity_type} · {r.reason} · {r.status} · {formatDate(r.created_at)}</p>)}
            {data.reports_against.map((r) => <p key={r.id} className="text-xs text-red-700"><b>Wurde gemeldet:</b> {r.reason} · {r.status} · {formatDate(r.created_at)}</p>)}
            {data.strikes.map((s) => <p key={s.id} className="text-xs text-red-700"><b>Strike:</b> {s.reason || s.type} · {formatDate(s.created_at)}</p>)}
          </div>
        </Section>
        <Section title="Interne Notizen" count={data.notes.length}>
          <div className="space-y-2">
            {data.notes.map((n) => (
              <p key={n.id} className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"><b>{n.author_name}</b> ({fmtDateTime(n.created_at)}): {n.body}</p>
            ))}
            <div className="flex gap-2">
              <input className={inputCls} placeholder="Neue interne Notiz…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="customer-note-input" />
              <button className={btnGhost} disabled={!note.trim()} data-testid="customer-note-btn"
                onClick={async () => { try { await api.post(`/admin/customers/${userId}/notes`, { body: note }); setNote(""); load(); } catch (e) { toast.error(errText(e)); } }}>Speichern</button>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}

export default function PanelCustomers({ openTicket }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [list, setList] = useState(null);
  const [page, setPage] = useState(1);
  const [onlyCustomers, setOnlyCustomers] = useState(true);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/admin/customers/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data.customers)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    api.get(`/admin/customers?page=${page}&limit=25${onlyCustomers ? "&role=customers" : ""}`).then(({ data }) => setList(data)).catch(() => {});
  }, [page, onlyCustomers, selected]);

  const searching = q.trim().length >= 2;
  const rows = searching ? results : (list?.customers || []);

  if (selected) return <Profile userId={selected} onBack={() => setSelected(null)} openTicket={openTicket} />;
  return (
    <div className="space-y-4" data-testid="panel-customers">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-lg min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className={`${inputCls} pl-10`} placeholder="Kunde suchen (Name, Benutzername, E-Mail)…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search-input" />
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={onlyCustomers} onChange={(e) => { setOnlyCustomers(e.target.checked); setPage(1); }} className="accent-[#FF6B35]" data-testid="customers-only-toggle" />
          Nur Kunden (ohne Team)
        </label>
        {!searching && list && <span className="text-xs text-muted-foreground ml-auto">{list.total} {onlyCustomers ? "Kunden" : "registrierte Nutzer"}</span>}
      </div>
      <div className="space-y-2" data-testid="customer-results">
        {!searching && list === null && <div className="h-40 bg-muted rounded-2xl animate-pulse" />}
        {searching && results.length === 0 && <p className="text-sm text-muted-foreground">Keine Treffer.</p>}
        {rows.map((u) => (
          <button key={u.id} onClick={() => setSelected(u.id)} className={`${card} w-full flex items-center gap-3 p-3.5 text-left hover:border-accent/50`} data-testid={`customer-result-${u.username}`}>
            <span className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-bold shrink-0">{(u.name || u.username || "?").slice(0, 2).toUpperCase()}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-bold truncate">{u.name || u.username} <span className="font-normal text-muted-foreground">@{u.username}</span></span>
              <span className="block text-xs text-muted-foreground truncate">{u.email} · seit {formatDate(u.created_at)}</span>
            </span>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${u.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{u.status}</span>
          </button>
        ))}
      </div>
      {!searching && list && list.pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <button className={btnGhost} disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="customers-prev-btn">Zurück</button>
          <span className="text-xs text-muted-foreground">Seite {list.page} von {list.pages}</span>
          <button className={btnGhost} disabled={page >= list.pages} onClick={() => setPage(page + 1)} data-testid="customers-next-btn">Weiter</button>
        </div>
      )}
    </div>
  );
}
